import { useState, type ReactNode } from 'react';
import { Button } from 'react-aria-components/Button';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { useOverrideDocumentTitle } from '../../shared/accessibility/DocumentTitle';
import { authErrorText, reportUnknownAuthError } from './auth-errors';
import { useSession } from './SessionContext';

interface PasswordFormValues {
  identifier: string;
  password: string;
}

interface SessionGateProps {
  children: ReactNode;
  /** Deja pasar sin sesión: para una pantalla pública como el formulario de contacto. */
  allowAnonymous?: boolean;
}

export function SessionGate({ children, allowAnonymous = false }: SessionGateProps) {
  const { state, refresh, signIn, signInWithPassword } = useSession();
  const [loginPending, setLoginPending] = useState(false);
  const [loginError, setLoginError] = useState('');
  const passwordForm = useForm<PasswordFormValues>({ defaultValues: { identifier: '', password: '' } });
  const overrideTitle =
    state.status === 'unavailable' && !allowAnonymous
      ? 'Error al iniciar tu aventura'
      // Sin sesión, la pantalla de login tapa cualquier ruta protegida — no
      // hay razón para que el título de la pestaña revele qué ruta es (p. ej.
      // "Estado del sistema" en /system) antes de saber si la cuenta puede
      // siquiera entrar.
      : state.status === 'anonymous' && !allowAnonymous
        ? 'Inicia sesión'
        : undefined;
  useOverrideDocumentTitle(overrideTitle);

  if (state.status === 'checking' && !allowAnonymous) {
    return (
      <section className="state-panel">
        <div className="rs-loading" role="status">
          <span className="rs-spinner" aria-hidden="true" />
          <span>Cargando…</span>
        </div>
      </section>
    );
  }

  if (state.status === 'unavailable' && !allowAnonymous) {
    return (
      <section className="state-panel">
        <h1>La aventura quedó del otro lado</h1>
        <p>Intentamos abrirte el camino, pero la conexión se interrumpió antes de que pudiéramos completar el acceso.</p>
        <p>A veces basta con intentarlo otra vez para que todo vuelva a responder. Si el problema continúa, puede que Rol Sensorial esté teniendo dificultades para conectarse en este momento.</p>
        <Button className="btn btn-accent" onPress={() => void refresh()}>
          Intentar nuevamente
        </Button>
      </section>
    );
  }

  if (state.status === 'anonymous' && !allowAnonymous) {
    const oauthFailed = new URLSearchParams(window.location.search).get('auth') === 'failed';
    const startLogin = async () => {
      setLoginPending(true);
      setLoginError('');
      try {
        await signIn();
      } catch (error) {
        setLoginPending(false);
        console.error('[Rol Sensorial] No se pudo iniciar el acceso con Google.', error);
        reportUnknownAuthError('sign-in-google', error);
        setLoginError('No pudimos abrir el acceso con Google. Inténtalo de nuevo.');
      }
    };

    const onSubmitPassword = async (values: PasswordFormValues) => {
      setLoginError('');
      try {
        await signInWithPassword(values.identifier.trim(), values.password);
      } catch (error) {
        console.error('[Rol Sensorial] No se pudo iniciar el acceso con usuario y contraseña.', error);
        reportUnknownAuthError('sign-in-password', error);
        setLoginError(authErrorText(error));
      }
    };

    return (
      <section className="login-panel">
        <div>
          <p className="eyebrow">Tu mesa empieza aquí</p>
          <h1>Entra a Rol Sensorial</h1>
          <p className="lead">
            Accede con la misma cuenta que utilizas en Rol Sensorial. Si ya tenías una sesión,
            la aplicación la recupera automáticamente.
          </p>
          {oauthFailed && !loginError ? (
            <p role="alert" className="error-message">
              No se pudo completar el acceso con Google. Puedes volver a intentarlo.
            </p>
          ) : null}
          {loginError ? <p role="alert" className="error-message">{loginError}</p> : null}
          <Button
            className="btn btn-accent"
            onPress={() => void startLogin()}
            isPending={loginPending}
          >
            {loginPending ? 'Abriendo el acceso…' : 'Continuar con Google'}
          </Button>

          <p className="login-panel-divider">o con tu usuario</p>

          <form className="rs-form" noValidate onSubmit={passwordForm.handleSubmit(onSubmitPassword)}>
            <div className="mb-3">
              <label className="form-label" htmlFor="login-identifier">Usuario o email</label>
              <input
                className="form-control"
                id="login-identifier"
                type="text"
                autoComplete="username"
                aria-invalid={passwordForm.formState.errors.identifier ? true : undefined}
                {...passwordForm.register('identifier', {
                  validate: (value) => value.trim().length > 0 || 'Escribe tu usuario o tu email.',
                })}
              />
              {passwordForm.formState.errors.identifier ? (
                <p className="rs-error" role="alert">{passwordForm.formState.errors.identifier.message}</p>
              ) : null}
            </div>

            <div className="mb-3">
              <label className="form-label" htmlFor="login-password">Contraseña</label>
              <input
                className="form-control"
                id="login-password"
                type="password"
                autoComplete="current-password"
                aria-invalid={passwordForm.formState.errors.password ? true : undefined}
                {...passwordForm.register('password', {
                  validate: (value) => value.length > 0 || 'Escribe tu contraseña.',
                })}
              />
              {passwordForm.formState.errors.password ? (
                <p className="rs-error" role="alert">{passwordForm.formState.errors.password.message}</p>
              ) : null}
            </div>

            <button className="btn btn-outline-secondary" type="submit" disabled={passwordForm.formState.isSubmitting}>
              {passwordForm.formState.isSubmitting ? 'Entrando…' : 'Entrar'}
            </button>
          </form>

          <p className="login-panel-links">
            <Link to="/register">Crear una cuenta</Link> · <Link to="/forgot-password">Olvidé mi contraseña</Link>
          </p>
        </div>
        <aside className="welcome-note">
          <h2>Un espacio para jugar y crear</h2>
          <p>Entra para participar en la comunidad y acceder a tus espacios.</p>
        </aside>
      </section>
    );
  }

  return children;
}
